import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, KeyRound, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 px-4">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8">
      <Link to="/login" className="text-sm text-stone-500 flex items-center gap-1 mb-6 hover:text-stone-700"
        data-testid="back-to-login">
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </Link>
      <h1 className="text-2xl font-semibold text-stone-800 mb-1">{title}</h1>
      {subtitle && <p className="text-sm text-stone-500 mb-6">{subtitle}</p>}
      {children}
    </div>
  </div>
);

/* ---------------- Forgot Password ---------------- */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/account/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Server always returns 200 — anything else means a network / config problem
      toast.error(err.response?.data?.detail || 'Could not reach the server.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <Shell title="Check your inbox" subtitle="If an account exists for that email, a password reset link is on its way. The link is valid for 24 hours.">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-800">
            We&apos;ve sent a reset link to <b>{email}</b>. If you don&apos;t see it within a few minutes, check your spam folder.
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Forgot your password?" subtitle="Enter your account email and we'll send you a reset link.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
        <div>
          <Label>Email address</Label>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" />
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="pl-9" placeholder="you@example.com" required autoFocus
              data-testid="forgot-password-email" />
          </div>
        </div>
        <Button type="submit" disabled={submitting} className="w-full"
          data-testid="forgot-password-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Send reset link
        </Button>
      </form>
    </Shell>
  );
}

/* ---------------- Forgot Email ---------------- */
export function ForgotEmail() {
  const [lookup, setLookup] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!lookup.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/account/forgot-email`, { lookup: lookup.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reach the server.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <Shell title="Check your inbox" subtitle="If we found a matching account, we've emailed the sign-in address to it.">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-800">
            If a Coffee Crews account matches <b>{lookup}</b>, your sign-in email is on its way.
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Forgot your email?"
      subtitle="Enter your phone number or SimplePay employee code and we'll email you the sign-in address on file.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-email-form">
        <div>
          <Label>Phone number or employee code</Label>
          <Input value={lookup} onChange={e => setLookup(e.target.value)}
            placeholder="+27 82 123 4567  or  0044" required autoFocus
            data-testid="forgot-email-lookup" />
        </div>
        <Button type="submit" disabled={submitting} className="w-full"
          data-testid="forgot-email-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Email me my sign-in address
        </Button>
      </form>
    </Shell>
  );
}

/* ---------------- Reset Password (also used for "Set your password") ---------------- */
export function ResetPassword({ mode = 'reset' }) {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/account/reset-token/${token}`);
        setValid(!!r.data?.valid);
        setUserInfo(r.data);
      } catch { setValid(false); }
      finally { setChecking(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (pw1 !== pw2) { toast.error('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/account/reset-password`, { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update password.');
    } finally { setSubmitting(false); }
  };

  const isSetup = mode === 'setup';
  const title = isSetup ? 'Set your password' : 'Reset your password';

  if (checking) {
    return <Shell title={title}><div className="text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking link…</div></Shell>;
  }
  if (!token || !valid) {
    return (
      <Shell title="Link expired or invalid" subtitle="This link is no longer valid. Please request a new one.">
        <div className="flex gap-2 mt-2">
          <Button asChild variant="outline"><Link to="/forgot-password">Request new link</Link></Button>
          <Button asChild variant="ghost"><Link to="/login">Back to sign in</Link></Button>
        </div>
      </Shell>
    );
  }
  if (done) {
    return (
      <Shell title="Password updated" subtitle="You can now sign in with your new password. Redirecting…">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div className="text-sm text-emerald-800">All set — taking you to sign in.</div>
        </div>
      </Shell>
    );
  }
  return (
    <Shell title={title}
      subtitle={userInfo?.email ? `For ${userInfo.email}` : 'Choose a strong password (min 8 characters).'}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
        <div>
          <Label>New password</Label>
          <div className="relative">
            <KeyRound className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" />
            <Input type="password" value={pw1} onChange={e => setPw1(e.target.value)}
              className="pl-9" minLength={8} required autoFocus
              data-testid="reset-password-new" />
          </div>
        </div>
        <div>
          <Label>Confirm new password</Label>
          <div className="relative">
            <KeyRound className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" />
            <Input type="password" value={pw2} onChange={e => setPw2(e.target.value)}
              className="pl-9" minLength={8} required
              data-testid="reset-password-confirm" />
          </div>
        </div>
        <Button type="submit" disabled={submitting} className="w-full"
          data-testid="reset-password-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {isSetup ? 'Set password' : 'Update password'}
        </Button>
      </form>
    </Shell>
  );
}

export default { ForgotPassword, ForgotEmail, ResetPassword };
